import { useEffect, useState } from "react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import type { MachineDetail } from "@/features/lines/hooks";
import { useCreateMachine } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useMachineSidebarBridge } from "../hooks/useMachineSidebarBridge";
import { isDraftLineId } from "@/features/lines/canvas/draft-canvas-adapter";
import type { MachineChangeEvent, SidebarContext, SidebarMode } from "../MachineSidebar";
import { IdentitySection } from "./IdentitySection";
import { StatusSection } from "./StatusSection";
import { toForm, type BasicForm } from "./types";

interface Props {
  machine: MachineDetail | undefined;
  lineId: string;
  mode: SidebarMode;
  context: SidebarContext;
  onMachineChange?: (event: MachineChangeEvent) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function BasicDataTab({
  machine,
  lineId,
  mode,
  context,
  onMachineChange,
  onDirtyChange,
}: Props) {
  const isView = mode === "view";
  const isCanvasAdd = context === "canvas-add" && !machine;

  const [form, setForm] = useState<BasicForm>(() => toForm(machine));
  const [isDirty, setIsDirty] = useState(false);

  const bridge = useMachineSidebarBridge(machine?.id, lineId);
  const isDraft = isDraftLineId(lineId);
  const create = useCreateMachine(lineId);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local state com prop quando machine muda
    setForm(toForm(machine));
    setIsDirty(false);
    onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine?.id]);

  function patch<K extends keyof BasicForm>(key: K, value: BasicForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (!isDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
  }

  async function handleSave() {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      type: form.type,
      requiresOperator: form.requiresOperator,
      stoppedCostPerHour:
        form.stoppedCostPerHour.trim() === "" ? undefined : Number(form.stoppedCostPerHour),
    };
    if (!payload.code || !payload.name) {
      toast.error("Informe código e nome.");
      return;
    }
    try {
      if (isCanvasAdd && !isDraft) {
        const created = await create.mutateAsync(payload);
        toast.success("Máquina criada.");
        setIsDirty(false);
        onDirtyChange?.(false);
        onMachineChange?.({ type: "created", machineId: created.id, name: created.name });
      } else if (machine) {
        const renamed = payload.name !== machine.name;
        await bridge.updateBasic({
          name: payload.name,
          code: payload.code,
          type: payload.type,
          requiresOperator: payload.requiresOperator,
          stoppedCostPerHour: payload.stoppedCostPerHour ?? null,
          // Spec 078 — só envia quando mudou (undefined mantém).
          // Spec 080 — o `isDraft` saiu da condição: em rascunho o campo era descartado de
          // propósito porque nada o gravava do outro lado. Agora o rascunho o carrega.
          ...(form.model3dId === (machine.model3dId ?? "")
            ? {}
            : { model3dId: form.model3dId === "" ? null : form.model3dId }),
        });
        toast.success(isDraft ? "Alterações aplicadas ao rascunho." : "Dados básicos salvos.");
        setIsDirty(false);
        onDirtyChange?.(false);
        if (renamed) {
          onMachineChange?.({ type: "renamed", machineId: machine.id, name: payload.name });
        }
      }
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao salvar.", { description: msg });
    }
  }

  function handleCancel() {
    setForm(toForm(machine));
    setIsDirty(false);
    onDirtyChange?.(false);
  }

  const isSaving = create.isPending;

  return (
    <div className="space-y-4">
      <IdentitySection form={form} isView={isView} patch={patch} />
      <StatusSection machine={machine} />
      {!isView && isDirty ? (
        <div className="flex gap-2 border-t pt-3">
          <Button className="flex-1" onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? "Salvando…" : isCanvasAdd ? "Criar e vincular" : "Salvar"}
          </Button>
          <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
            Cancelar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
